import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';

interface PendingRequest {
  id?: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  timestamp: number;
}

interface OfflineQueueDB extends DBSchema {
  'pending-requests': {
    key: number;
    value: PendingRequest;
  };
}

let dbInstance: IDBPDatabase<OfflineQueueDB> | null = null;

async function getDB(): Promise<IDBPDatabase<OfflineQueueDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<OfflineQueueDB>('umland-offline-queue', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('pending-requests')) {
        db.createObjectStore('pending-requests', { keyPath: 'id', autoIncrement: true });
      }
    },
  });

  return dbInstance;
}

export async function addPendingRequest(
  url: string,
  method: string,
  headers: Record<string, string>,
  body?: string,
): Promise<number> {
  const db = await getDB();
  return db.add('pending-requests', {
    url,
    method,
    headers,
    body,
    timestamp: Date.now(),
  });
}

export async function getPendingRequests(): Promise<PendingRequest[]> {
  const db = await getDB();
  return db.getAll('pending-requests');
}

export async function deletePendingRequest(id: number): Promise<void> {
  const db = await getDB();
  await db.delete('pending-requests', id);
}

export async function clearAllPendingRequests(): Promise<void> {
  const db = await getDB();
  await db.clear('pending-requests');
}

export async function syncPendingRequests(
  onSuccess?: (url: string) => void,
  onError?: (url: string, error: Error) => void,
): Promise<{ successful: number; failed: number }> {
  const db = await getDB();
  const requests = await db.getAll('pending-requests');

  let successful = 0;
  let failed = 0;

  for (const request of requests) {
    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: request.headers,
        body: request.body,
      });

      if (response.ok && request.id) {
        await db.delete('pending-requests', request.id);
        successful++;
        onSuccess?.(request.url);
      } else {
        failed++;
        onError?.(request.url, new Error(`HTTP ${response.status}`));
      }
    } catch (error) {
      failed++;
      onError?.(request.url, error instanceof Error ? error : new Error(String(error)));
    }
  }

  return { successful, failed };
}

export async function notifyServiceWorker(): Promise<void> {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_OFFLINE_REQUESTS',
    });
  }
}

export async function registerBackgroundSync(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const syncManager = (registration as ServiceWorkerRegistration & { sync?: { register: (tag: string) => Promise<void> } }).sync;
      if (syncManager) {
        await syncManager.register('sync-offline-requests');
      }
    } catch (error) {
      console.error('Failed to register background sync:', error);
    }
  }
}
